import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Stack, Typography, CircularProgress, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { apiConcurso } from '../../api/cliente';
import { abrirArquivoFormulario } from '../../api/formulario';
import { extrairMensagemErro } from '../../utils/apiError';

/**
 * Campo de upload de arquivos. Suporta múltiplos arquivos por campo.
 * Valor armazenado como JSON: [{ path, nome, tamanho }, ...]
 *
 * Upload vai pra container privado do tenant via POST /formulario/arquivos/upload.
 * Acesso ao arquivo posterior é via GET /formulario/arquivos?path=... que redireciona
 * pra SAS URL efêmera (10min) — link nunca expõe o blob público.
 */
export default function FormFieldFile({ campo, valor, onChange, disabled, fieldSize = 'medium' }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);

  const cfg = campo.configuracao || {};
  const aceitos = cfg.aceitos && cfg.aceitos.length > 0 ? cfg.aceitos : ['pdf', 'jpg', 'jpeg', 'png'];
  const accept = aceitos.map((e) => '.' + e).join(',');
  const multiplos = cfg.multiplos !== false; // default true
  const tamanhoMaxMb = cfg.tamanhoMaxMb || 10;

  // Parse defensivo do valor (pode vir string JSON ou array)
  const arquivos = (() => {
    if (!valor) return [];
    if (Array.isArray(valor)) return valor;
    try {
      const parsed = JSON.parse(valor);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  const persistir = (lista) => {
    onChange(campo.codigo, JSON.stringify(lista));
  };

  const handleSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setErro(null);
    setEnviando(true);
    try {
      const novos = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('arquivo', file);
        const res = await apiConcurso.post('/api/v1/formulario/arquivos/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const data = res.data?.data;
        if (data?.path) novos.push(data);
      }
      persistir(multiplos ? [...arquivos, ...novos] : novos);
    } catch (err) {
      setErro(extrairMensagemErro(err, t('arquivo.erroUpload')));
    } finally {
      setEnviando(false);
      if (inputRef.current) inputRef.current.value = ''; // permite re-selecionar mesmo arquivo
    }
  };

  const handleRemover = (idx) => {
    const lista = arquivos.filter((_, i) => i !== idx);
    persistir(lista);
  };

  const handleAbrir = async (path) => {
    try {
      await abrirArquivoFormulario(path);
    } catch (err) {
      setErro(extrairMensagemErro(err, t('arquivo.erroAbrir')));
    }
  };

  const formatTamanho = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
        {campo.label || campo.codigo}
        {campo.obrigatorio && <span style={{ color: '#d32f2f' }}> *</span>}
      </Typography>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiplos}
        style={{ display: 'none' }}
        onChange={handleSelect}
        disabled={disabled || enviando}
      />

      <Button
        variant="outlined"
        size={fieldSize}
        startIcon={enviando ? <CircularProgress size={18} /> : <CloudUploadIcon />}
        onClick={() => inputRef.current?.click()}
        disabled={disabled || enviando || (!multiplos && arquivos.length > 0)}
        fullWidth
        sx={{ justifyContent: 'flex-start' }}
      >
        {enviando
          ? t('arquivo.enviando')
          : arquivos.length > 0 && !multiplos
            ? t('arquivo.substituir')
            : t('arquivo.selecionar')}
      </Button>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        {aceitos.map((a) => a.toUpperCase()).join(', ')} • {t('arquivo.ate', { mb: tamanhoMaxMb })}
        {' • '}
        {multiplos ? t('arquivo.multiplos') : t('arquivo.um')}
      </Typography>

      {erro && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}

      {arquivos.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
          {arquivos.map((a, idx) => (
            <Box
              key={a.path || idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap title={a.nome}>
                  {a.nome || a.path}
                </Typography>
                {a.tamanho > 0 && (
                  <Typography variant="caption" color="text.disabled">
                    {formatTamanho(a.tamanho)}
                  </Typography>
                )}
              </Box>
              <IconButton size="small" title={t('arquivo.abrir')} onClick={() => handleAbrir(a.path)}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
              {!disabled && (
                <IconButton size="small" color="error" onClick={() => handleRemover(idx)} title={t('arquivo.remover')}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
